import apiClient from './client';

// ── Types ──

export interface AdminOrg {
  id: string;
  name: string;
  email: string | null;
  is_blocked: boolean;
  site_count: number;
  user_count: number;
  created_at: string;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  is_active: boolean;
  organization_id: string | null;
  org_name: string | null;
  created_at: string;
}

export interface AdminSite {
  id: string;
  name: string;
  domain: string;
  live_support_enabled: boolean;
  created_at: string;
}

export interface AIStats {
  total_sites: number;
  live_support_sites: number;
  blocked_orgs: number;
  total_orgs: number;
  total_users: number;
}

export type SupportTicketStatus = 'open' | 'dispatched' | 'in_progress' | 'resolved' | 'closed';

export interface AdminSupportSite {
  id: string;
  name: string;
  domain: string;
  organization_name: string | null;
  escalation_enabled: boolean;
}

export interface AdminSupportTicket {
  id: string;
  site_id: string;
  site_name: string;
  site_domain: string;
  organization_name: string | null;
  visitor_id: string | null;
  trigger: string;
  status: SupportTicketStatus;
  channel: string;
  summary: string;
  user_message: string | null;
  page_url: string | null;
  conversation_history: Array<{ role?: string; content?: string; [key: string]: unknown }>;
  channel_response: Record<string, unknown>;
  ai_confidence: number | null;
  created_at: string;
  updated_at: string;
}

export interface AdminSupportTicketList {
  items: AdminSupportTicket[];
  total: number;
  page: number;
  pages: number;
  status_counts: Record<string, number>;
}

// ── Organization APIs ──

export async function listOrganizations(): Promise<AdminOrg[]> {
  const { data } = await apiClient.get<AdminOrg[]>('/api/v1/admin/organizations');
  return data;
}

export async function updateOrganization(
  orgId: string,
  payload: { name?: string; is_blocked?: boolean; restart_trial?: boolean },
) {
  const { data } = await apiClient.patch(`/api/v1/admin/organizations/${orgId}`, payload);
  return data;
}

export async function listOrgSites(orgId: string): Promise<AdminSite[]> {
  const { data } = await apiClient.get<AdminSite[]>(`/api/v1/admin/organizations/${orgId}/sites`);
  return data;
}

// ── User APIs ──

export async function listUsers(): Promise<AdminUser[]> {
  const { data } = await apiClient.get<AdminUser[]>('/api/v1/admin/users');
  return data;
}

export async function updateUser(
  userId: string,
  payload: { role?: string; is_active?: boolean },
) {
  const { data } = await apiClient.patch(`/api/v1/admin/users/${userId}`, payload);
  return data;
}

// ── Support Ticket APIs ──

export async function listSupportSites(): Promise<AdminSupportSite[]> {
  const { data } = await apiClient.get<AdminSupportSite[]>('/api/v1/admin/support-sites');
  return data;
}

export async function listSupportTickets(params: {
  status?: string;
  site_id?: string;
  search?: string;
  page?: number;
  page_size?: number;
}): Promise<AdminSupportTicketList> {
  const { data } = await apiClient.get<AdminSupportTicketList>('/api/v1/admin/support-tickets', {
    params,
  });
  return data;
}

export async function createTestSupportTicket(siteId: string): Promise<AdminSupportTicket> {
  const { data } = await apiClient.post<AdminSupportTicket>('/api/v1/admin/support-tickets/test', {
    site_id: siteId,
  });
  return data;
}

export async function updateSupportTicketStatus(
  ticketId: string,
  status: SupportTicketStatus,
): Promise<AdminSupportTicket> {
  const { data } = await apiClient.patch<AdminSupportTicket>(
    `/api/v1/admin/support-tickets/${ticketId}`,
    { status },
  );
  return data;
}

// ── Subscription APIs ──

export interface SubscriptionPlan {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  price_cents: number;
  billing_interval: string;
  monthly_event_limit: number;
  monthly_ai_token_limit: number;
  monthly_session_limit: number;
  max_sites: number;
  max_seats: number;
  max_voice_clones: number;
  data_retention_days: number;
  event_overage_policy: string;
  event_overage_price_cents: number;
  features: string[] | null;
  /** Admin-set gates. A key that is absent uses the shipped default. */
  entitlements: Record<string, boolean> | null;
  /** Admin-set per-period allowances (-1 unlimited). Absent = shipped default. */
  quotas: Record<string, number> | null;
  /** Sales-gated: the pricing page shows "Book a demo", not a price. */
  contact_sales: boolean;
  /** Smallest accepted credit purchase, in USD cents (0 = no minimum). */
  min_topup_cents: number;
  /** USD cents per 1,000 credits (0 = credits are not sold directly). */
  credit_price_cents_per_1000: number;
  trial_days: number;
  is_active: boolean;
  display_order: number;
  // Credit system fields
  monthly_credits: number;
  credit_rate_multiplier: number;
  credit_overage_policy: string;
  byok_allowed: boolean;
  created_at: string;
  updated_at: string;
}

export interface SubscriptionPlanCreate {
  name: string;
  slug: string;
  description?: string;
  price_cents?: number;
  billing_interval?: string;
  monthly_event_limit?: number;
  monthly_ai_token_limit?: number;
  monthly_session_limit?: number;
  max_sites?: number;
  max_seats?: number;
  max_voice_clones?: number;
  data_retention_days?: number;
  event_overage_policy?: string;
  event_overage_price_cents?: number;
  features?: string[];
  entitlements?: Record<string, boolean>;
  quotas?: Record<string, number>;
  contact_sales?: boolean;
  min_topup_cents?: number;
  credit_price_cents_per_1000?: number;
  trial_days?: number;
  is_active?: boolean;
  display_order?: number;
  // Credit system fields
  monthly_credits?: number;
  credit_rate_multiplier?: number;
  credit_overage_policy?: string;
  byok_allowed?: boolean;
}

export interface SubscriptionResponse {
  id: string;
  organization_id: string;
  plan: string;
  plan_id: string | null;
  monthly_token_limit: number;
  monthly_request_limit: number;
  monthly_event_limit: number;
  monthly_ai_token_limit: number;
  monthly_session_limit: number;
  max_sites: number;
  max_seats: number;
  data_retention_days: number;
  bonus_tokens: number;
  bonus_requests: number;
  // Credit system fields
  monthly_credit_limit: number;
  credits_used: number;
  bonus_credits: number;
  byok_enabled: boolean;
  current_period_start: string;
  current_period_end: string;
  created_at: string;
  updated_at: string;
}

export interface UsagePeriodStats {
  tokens_used: number;
  requests_used: number;
  events_used: number;
  sessions_used: number;
  sites_used: number;
  credits_used: number;
  credits_remaining: number;
  credit_usage_percent: number;
  period_start: string;
  period_end: string;
  days_remaining: number;
}

export interface EffectiveLimits {
  total_token_limit: number;
  total_request_limit: number;
  total_event_limit: number;
  total_ai_token_limit: number;
  total_session_limit: number;
  max_sites: number;
  max_seats: number;
  total_credit_limit: number;
  token_usage_percent: number;
  request_usage_percent: number;
  event_usage_percent: number;
  ai_token_usage_percent: number;
  session_usage_percent: number;
  credit_usage_percent: number;
}

export interface SubscriptionWithUsage {
  subscription: SubscriptionResponse;
  usage: UsagePeriodStats;
  effective_limits: EffectiveLimits;
}

export async function getOrgSubscription(orgId: string): Promise<SubscriptionWithUsage> {
  const { data } = await apiClient.get<SubscriptionWithUsage>(`/api/v1/subscriptions/org/${orgId}`);
  return data;
}

export async function updateOrgPlan(orgId: string, planIdOrSlug: string, usePlanId = false): Promise<SubscriptionResponse> {
  const payload = usePlanId ? { plan_id: planIdOrSlug } : { plan: planIdOrSlug };
  const { data } = await apiClient.patch<SubscriptionResponse>(`/api/v1/subscriptions/org/${orgId}/plan`, payload);
  return data;
}

export async function addOrgCredits(
  orgId: string,
  payload: { bonus_tokens: number; bonus_requests: number; reason?: string },
): Promise<{ organization_id: string; bonus_tokens: number; bonus_requests: number; message: string }> {
  const { data } = await apiClient.post(`/api/v1/subscriptions/org/${orgId}/credits`, payload);
  return data;
}

// ── Subscription Plan APIs ──

/** One gate a plan can configure, as described by the API. */
export interface CapabilityEntry {
  key: string;
  label: string;
  description: string;
  /** Features only: the tier that unlocks this by default. */
  default_min_plan?: string;
  /** Plan slugs whose shipped default is not the plain rank comparison. */
  plan_overrides?: Record<string, boolean>;
}

export interface CapabilityCatalogue {
  features: CapabilityEntry[];
  quotas: CapabilityEntry[];
}

/**
 * Every gate the API can check, with labels.
 *
 * The plan editor renders from this rather than a hard-coded list, so a gate
 * added on the server shows up here without a matching front-end change.
 */
export async function fetchCapabilityCatalogue(): Promise<CapabilityCatalogue> {
  const { data } = await apiClient.get<CapabilityCatalogue>(
    '/api/v1/subscription-plans/capability-catalogue',
  );
  return data;
}

export async function listAllPlans(): Promise<SubscriptionPlan[]> {
  const { data } = await apiClient.get<SubscriptionPlan[]>('/api/v1/subscription-plans/all');
  return data;
}

export async function listActivePlans(): Promise<SubscriptionPlan[]> {
  const { data } = await apiClient.get<SubscriptionPlan[]>('/api/v1/subscription-plans');
  return data;
}

export async function createPlan(payload: SubscriptionPlanCreate): Promise<SubscriptionPlan> {
  const { data } = await apiClient.post<SubscriptionPlan>('/api/v1/subscription-plans', payload);
  return data;
}

export async function updatePlan(planId: string, payload: Partial<SubscriptionPlanCreate>): Promise<SubscriptionPlan> {
  const { data } = await apiClient.patch<SubscriptionPlan>(`/api/v1/subscription-plans/${planId}`, payload);
  return data;
}

export async function deletePlan(planId: string): Promise<{ status: string }> {
  const { data } = await apiClient.delete<{ status: string }>(`/api/v1/subscription-plans/${planId}`);
  return data;
}

export interface CreditFeatureRate {
  feature_key: string;
  label: string;
  category: string;
  base_credits: number;
  unit_label: string;
  description: string | null;
  display_order: number;
  is_active: boolean;
}

export async function listCreditRates(): Promise<CreditFeatureRate[]> {
  const { data } = await apiClient.get<CreditFeatureRate[]>('/api/v1/subscription-plans/credit-rates');
  return data;
}

export async function updateCreditRates(
  rates: Array<{ feature_key: string; base_credits: number }>,
): Promise<CreditFeatureRate[]> {
  const { data } = await apiClient.put<CreditFeatureRate[]>('/api/v1/subscription-plans/credit-rates', { rates });
  return data;
}

// ── AI Stats ──

export async function getAIStats(): Promise<AIStats> {
  const { data } = await apiClient.get<AIStats>('/api/v1/admin/ai-stats');
  return data;
}

// ── Credit & Usage Monitoring APIs ──

export interface UsageOverview {
  total_credits: number;
  total_tokens: number;
  total_requests: number;
  avg_latency_ms: number;
  active_orgs_today: number;
  total_estimated_cost: number;
  total_subscription_fee_cents: number;
  total_subscriptions: number;
  daily: Array<{ date: string; credits: number; tokens: number; requests: number; estimated_cost: number }>;
  top_models: Array<{ provider: string; model: string; credits: number; tokens: number; requests: number }>;
}

export interface CreditSummaryOrg {
  org_id: string;
  name: string;
  plan: string;
  plan_name: string;
  plan_price_cents: number;
  billing_interval: string;
  credits_used: number;
  credits_limit: number;
  monthly_credits: number;
  bonus: number;
  byok_enabled: boolean;
  days_remaining: number;
  percent_used: number;
}

export interface UsageAlert {
  org_id: string;
  org_name: string;
  type: string;
  message: string;
  severity: string;
}

export interface InteractionEntry {
  id: string;
  user_id: string | null;
  provider: string;
  model: string;
  feature: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  credits_consumed: number;
  used_byok: boolean;
  prompt_text: string | null;
  response_text: string | null;
  status: string;
  error_message: string | null;
  latency_ms: number | null;
  created_at: string;
}

export interface PaginatedInteractions {
  items: InteractionEntry[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

export async function getUsageOverview(days = 30): Promise<UsageOverview> {
  const { data } = await apiClient.get<UsageOverview>(`/api/v1/usage/admin/overview?days=${days}`);
  return data;
}

export async function getCreditsSummary(): Promise<{ orgs: CreditSummaryOrg[] }> {
  const { data } = await apiClient.get<{ orgs: CreditSummaryOrg[] }>('/api/v1/usage/admin/credits/summary');
  return data;
}

export async function getUsageAlerts(): Promise<{ alerts: UsageAlert[] }> {
  const { data } = await apiClient.get<{ alerts: UsageAlert[] }>('/api/v1/usage/admin/alerts');
  return data;
}

export async function getOrgInteractions(orgId: string, page = 1, pageSize = 50): Promise<PaginatedInteractions> {
  const { data } = await apiClient.get<PaginatedInteractions>(`/api/v1/usage/admin/org/${orgId}/interactions?page=${page}&page_size=${pageSize}`);
  return data;
}

export async function getOrgCreditHistory(orgId: string, page = 1): Promise<{ items: Array<{ id: string; transaction_type: string; credits_delta: number; credits_balance_after: number; description: string | null; created_at: string }>; total: number; page: number; pages: number }> {
  const { data } = await apiClient.get(`/api/v1/usage/admin/org/${orgId}/credit-history?page=${page}`);
  return data;
}

export async function addOrgCreditBalance(orgId: string, payload: { bonus_credits: number; reason?: string }): Promise<{ organization_id: string; bonus_credits: number; credits_remaining: number; message: string }> {
  const { data } = await apiClient.post(`/api/v1/subscriptions/org/${orgId}/add-credits`, payload);
  return data;
}

// ── Audit Log (platform activity trail) ──

export interface AuditLogEntry {
  id: string;
  created_at: string;
  user_id: string | null;
  actor_email: string | null;
  actor_name: string | null;
  actor_role: string | null;
  organization_id: string | null;
  org_name: string | null;
  site_id: string | null;
  site_domain: string | null;
  surface: string;
  category: string;
  action: string;
  target_type: string | null;
  target_id: string | null;
  target_label: string | null;
  route: string | null;
  website: string | null;
  method: string | null;
  status_code: number | null;
  duration_ms: number | null;
  ip_address: string | null;
  user_agent: string | null;
  details: Record<string, unknown>;
}

export interface AuditLogPage {
  items: AuditLogEntry[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

export interface AuditLogFilters {
  search?: string;
  user_id?: string;
  organization_id?: string;
  site_id?: string;
  category?: string;
  action?: string;
  surface?: string;
  days?: number;
  page?: number;
  page_size?: number;
}

export interface AuditFilterOptions {
  categories: string[];
  actions: string[];
  surfaces: string[];
  organizations: Array<{ id: string; name: string }>;
  users: Array<{ id: string; name: string; email: string }>;
}

export interface AuditCount {
  label: string;
  count: number;
}

export interface AuditSummary {
  days: number;
  total_events: number;
  active_users: number;
  active_orgs: number;
  guide_pro_events: number;
  guide_pro_websites: number;
  dashboard_events: number;
  top_actions: AuditCount[];
  top_users: AuditCount[];
  top_categories: AuditCount[];
  daily: Array<{ date: string | null; events: number; users: number }>;
}

export interface GuideProUsage {
  days: number;
  total_events: number;
  users: Array<{
    user_id: string | null;
    actor_email: string | null;
    actor_name: string | null;
    org_name: string | null;
    events: number;
    websites: string[];
    last_used_at: string | null;
  }>;
  websites: Array<{
    website: string;
    events: number;
    users: number;
    last_used_at: string | null;
  }>;
}

/** Strip empty filter values so the API sees only what the admin chose. */
function auditParams(filters: AuditLogFilters): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params[key] = value as string | number;
    }
  });
  return params;
}

export async function listAuditLogs(filters: AuditLogFilters): Promise<AuditLogPage> {
  const { data } = await apiClient.get<AuditLogPage>('/api/v1/admin/audit-logs', {
    params: auditParams(filters),
  });
  return data;
}

export async function getAuditFilterOptions(days = 30): Promise<AuditFilterOptions> {
  const { data } = await apiClient.get<AuditFilterOptions>('/api/v1/admin/audit-logs/filters', {
    params: { days },
  });
  return data;
}

export async function getAuditSummary(days = 7, organizationId?: string): Promise<AuditSummary> {
  const { data } = await apiClient.get<AuditSummary>('/api/v1/admin/audit-logs/summary', {
    params: auditParams({ days, organization_id: organizationId }),
  });
  return data;
}

export async function getGuideProUsage(days = 30, organizationId?: string): Promise<GuideProUsage> {
  const { data } = await apiClient.get<GuideProUsage>('/api/v1/admin/audit-logs/guide-pro', {
    params: auditParams({ days, organization_id: organizationId }),
  });
  return data;
}

/** Download the filtered trail as CSV via the browser. */
export async function downloadAuditCsv(filters: AuditLogFilters): Promise<void> {
  const { data } = await apiClient.get('/api/v1/admin/audit-logs/export', {
    params: auditParams({ ...filters, page: undefined, page_size: undefined }),
    responseType: 'blob',
  });
  const url = URL.createObjectURL(data as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// ── Skill visibility ──

export type SkillVisibility = 'public' | 'private';

export interface AdminSkill {
  name: string;
  title: string;
  description: string;
  category: string;
  hosts: string[];
  /** What the skill file says. */
  default_visibility: SkillVisibility;
  /** What applies now: the admin's choice if one was saved. */
  visibility: SkillVisibility;
  /** Organisations that see it while it is private. */
  organizations: { id: string; name: string }[];
}

export async function listSkillAccess(): Promise<AdminSkill[]> {
  const { data } = await apiClient.get<AdminSkill[]>('/api/v1/admin/skills');
  return data;
}

export async function updateSkillAccess(
  skillName: string,
  payload: { visibility: SkillVisibility; organization_ids: string[] },
): Promise<AdminSkill> {
  // Skill names carry a slash (aws/platform); the route takes it as a path.
  const path = skillName.split('/').map(encodeURIComponent).join('/');
  const { data } = await apiClient.put<AdminSkill>(`/api/v1/admin/skills/access/${path}`, payload);
  return data;
}
